import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import {
    getContent, getGate, updateContent, replaceFaqs,
    updateMediaAlt, updateMediaRole, publishContent, unpublishContent,
    getAuthors, attachMedia, detachMedia, deleteContent,
} from '../../api/cmsContents';
import { fetchWithAuth } from '../../api/apiClient';
import { BASE_URL } from '../../api/inquiries';
import { ACCEPT_IMAGE, IMAGE_HINT } from '../../constants/uploads';
import { getContentCategories } from '../../api/contentCategories';

/**
 * 콘텐츠 검수 편집기.
 *
 * 왼쪽에서 채우고 오른쪽에서 남은 조건을 본다. **판정은 전부 서버가 한다** —
 * 화면은 `/gate` 결과를 그대로 그린다. 같은 규칙을 클라이언트에도 두면 반드시
 * 어긋나고, 그때 어느 쪽이 맞는지 알 수 없게 된다.
 */
const TYPE_LABEL = { note: '기술노트', solution: '솔루션', product: '제품', case: '사례' };
const ROLE_LABEL = { hero: '대표', gallery: '갤러리', onsite: '현장 사진', diagram: '도식' };

const ko = (s) => [...(s ?? '')].length;

const ContentReviewEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [gate, setGate] = useState(null);
    const [categories, setCategories] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [form, setForm] = useState({});
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [uploading, setUploading] = useState(false);

    const reload = useCallback(async () => {
        const [c, g] = await Promise.all([getContent(id), getGate(id)]);
        setData(c);
        setGate(g);
        setForm({
            title: c.title ?? '',
            slug: c.slug ?? '',
            summary: c.summary ?? '',
            metaTitle: c.metaTitle ?? '',
            metaDescription: c.metaDescription ?? '',
            bodyMd: c.bodyMd ?? '',
            categoryId: c.categoryId ?? '',
            authorId: c.authorId ?? '',
        });
        setFaqs((c.faqs ?? []).map((f) => ({ question: f.question, answerMd: f.answerMd })));
        return c;
    }, [id]);

    useEffect(() => {
        (async () => {
            setLoading(true); setError('');
            try {
                const c = await reload();
                const [cats, auth] = await Promise.all([getContentCategories(c.type), getAuthors()]);
                setCategories(cats);
                setAuthors(auth);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [reload]);

    const run = async (fn, okMessage) => {
        setSaving(true); setError(''); setNotice('');
        try {
            await fn();
            await reload();
            if (okMessage) setNotice(okMessage);
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const upload = async (e, role) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const altText = window.prompt('이 이미지를 설명하는 문장을 적어 주세요. (대체텍스트 · 발행 조건)');
        if (!altText || !altText.trim()) return;

        setUploading(true); setError(''); setNotice('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=notes`, { method: 'POST', body: fd });
            if (!res.ok) throw new Error('업로드에 실패했습니다.');
            const up = await res.json();
            await attachMedia(id, { url: up.url || up.path, altText: altText.trim(), role });
            await reload();
            setNotice('이미지를 추가했습니다.');
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const save = () =>
        run(async () => {
            const patch = { ...form };
            patch.categoryId = patch.categoryId === '' ? null : Number(patch.categoryId);
            patch.authorId = patch.authorId === '' ? null : Number(patch.authorId);
            await updateContent(id, patch);
            await replaceFaqs(id, faqs);
        }, '저장했습니다.');

    const publish = async () => {
        setSaving(true); setError(''); setNotice('');
        try {
            await publishContent(id, false);
            await reload();
            setNotice('발행했습니다.');
        } catch (e) {
            // 경고만 남았으면 서버가 확인을 요구한다. 조용히 넘기면 경고를 둔 의미가 없다.
            if (e.needsAcknowledge) {
                const lines = (e.items ?? []).map((i) => `· ${i.label}${i.detail ? ` — ${i.detail}` : ''}`).join('\n');
                if (window.confirm(`${e.message}\n\n${lines}\n\n이대로 발행할까요?`)) {
                    try {
                        await publishContent(id, true);
                        await reload();
                        setNotice('경고를 확인하고 발행했습니다.');
                    } catch (e2) {
                        setError(e2.message);
                    }
                }
            } else {
                setError(e.message);
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <AdminLayout><div className="p-10 text-center text-gray-400 animate-pulse">불러오는 중…</div></AdminLayout>;
    }
    if (!data) {
        return <AdminLayout><div className="p-10 text-center text-gray-500">{error || '콘텐츠를 찾을 수 없습니다.'}</div></AdminLayout>;
    }

    const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
    const label = 'block text-xs font-bold text-gray-700 mb-1.5';
    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const metaTitleLen = ko((form.metaTitle || form.title || '') + ' | 가까운솔루션');
    const metaDescLen = ko(form.metaDescription);

    return (
        <AdminLayout>
            <div className="flex items-center gap-3 mb-6">
                <button onClick={() => navigate('/admin/contents')} className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50">&larr;</button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] font-bold">
                            {TYPE_LABEL[data.type] ?? data.type}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                            data.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                            {data.status === 'published' ? '발행됨' : '초안'}
                        </span>
                    </div>
                    <h2 className="text-xl font-extrabold text-gray-800 mt-1 truncate">{data.title}</h2>
                </div>
                <button
                    type="button" onClick={save} disabled={saving}
                    className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-bold disabled:opacity-40"
                >저장</button>
                {data.status === 'published' ? (
                    <button
                        type="button" onClick={() => run(() => unpublishContent(id), '발행을 취소했습니다.')} disabled={saving}
                        className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-bold disabled:opacity-40"
                    >발행 취소</button>
                ) : (
                    <>
                    {/*
                      초안만 지울 수 있다. 이전에는 삭제 기능이 없어서 '발행 취소'를 눌러도
                      글이 초안으로 남아 있었다 — 지운 줄 알았는데 목록에 그대로였다.
                    */}
                    <button
                        type="button"
                        disabled={saving}
                        onClick={async () => {
                            if (!window.confirm(`"${data.title}" 을(를) 삭제할까요?\n\n삭제한 글은 목록에서 사라지고 되살릴 수 없습니다.`)) return;
                            try {
                                await deleteContent(id);
                                navigate('/admin/contents');
                            } catch (e) {
                                setError(e.message);
                            }
                        }}
                        className="px-4 py-2 rounded-lg bg-white border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50 disabled:opacity-40"
                    >삭제</button>
                    <button
                        type="button" onClick={publish} disabled={saving || !gate?.canPublish}
                        title={gate?.canPublish ? '' : '필수 조건이 남아 있습니다'}
                        className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    >검사 후 발행</button>
                    </>
                )}
            </div>

            {error && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900 whitespace-pre-line">{error}</div>}
            {notice && <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">{notice}</div>}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5 items-start">
                {/* ── 채우는 곳 ─────────────────────────────────────────── */}
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                        <div>
                            <label className={label} htmlFor="c-title">제목</label>
                            <input id="c-title" className={input} value={form.title} onChange={set('title')} />
                        </div>
                        <div>
                            <label className={label} htmlFor="c-slug">
                                주소 <span className="font-normal text-gray-400">(URL · 발행 후 변경 금지)</span>
                            </label>
                            <input id="c-slug" className={`${input} font-mono`} value={form.slug} onChange={set('slug')} />
                        </div>
                        <div>
                            <label className={label} htmlFor="c-cat">카테고리</label>
                            <select id="c-cat" className={input} value={form.categoryId ?? ''} onChange={set('categoryId')}>
                                <option value="">선택 안 함</option>
                                {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                        </div>
                        {/* 기술노트는 저자가 발행 조건이다(06 N1). 다른 타입에는 강제하지 않는다. */}
                        <div>
                            <label className={label} htmlFor="c-author">
                                저자{' '}
                                <span className="font-normal text-gray-400">
                                    {data.type === 'note' ? '기술노트는 필수' : '선택'}
                                </span>
                            </label>
                            <select id="c-author" className={input} value={form.authorId ?? ''} onChange={set('authorId')}>
                                <option value="">선택 안 함</option>
                                {authors.filter((a) => a.isActive || a.id === form.authorId)
                                    .map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {a.displayName}{a.roleTitle ? ` · ${a.roleTitle}` : ''}
                                        </option>
                                    ))}
                            </select>
                            {authors.length === 0 && (
                                <p className="mt-1 text-[11px] text-amber-700">
                                    등록된 저자가 없습니다. 목록 화면의 &lsquo;저자 관리&rsquo;에서 먼저 추가하세요.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className={label} htmlFor="c-summary">
                                요약문 <span className="font-normal text-gray-400">목록 카드·RSS 용 · {ko(form.summary)}/220자</span>
                            </label>
                            <textarea id="c-summary" rows={3} className={input} value={form.summary} onChange={set('summary')} />
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                        <h3 className="text-sm font-extrabold text-gray-800">검색 노출</h3>
                        <div>
                            <label className={label} htmlFor="c-mt">
                                검색 제목{' '}
                                <span className={`font-normal ${metaTitleLen > 40 ? 'text-red-600' : metaTitleLen > 32 ? 'text-amber-600' : 'text-gray-400'}`}>
                                    접미사 포함 {metaTitleLen}자 · 32자 이하 권장, 40자 초과 차단
                                </span>
                            </label>
                            <input id="c-mt" className={input} value={form.metaTitle} onChange={set('metaTitle')} placeholder="비우면 제목을 씁니다" />
                        </div>
                        <div>
                            <label className={label} htmlFor="c-md">
                                검색 설명{' '}
                                <span className={`font-normal ${metaDescLen >= 70 && metaDescLen <= 110 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {metaDescLen}자 · 70~110자
                                </span>
                            </label>
                            <textarea id="c-md" rows={3} className={input} value={form.metaDescription} onChange={set('metaDescription')} />
                            {/* 요약문을 그대로 복사하면 길이 규격이 맞지 않아 게이트에서 막힌다(02 §2-5). */}
                            <p className="mt-1 text-[11px] text-gray-500">
                                요약문(220자)을 그대로 붙이지 마세요. 길이 규격이 달라 발행이 막힙니다.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-extrabold text-gray-800">
                                FAQ <span className="font-normal text-gray-400">{data.type === 'note' ? '기술노트는 2~4개 필수' : '1개 이상 권장'}</span>
                            </h3>
                            <button
                                type="button"
                                onClick={() => setFaqs((p) => [...p, { question: '', answerMd: '' }])}
                                className="text-xs font-bold text-primary-500"
                            >+ 추가</button>
                        </div>
                        {faqs.length === 0 && <p className="text-xs text-gray-400">아직 없습니다.</p>}
                        <div className="space-y-3">
                            {faqs.map((f, i) => (
                                <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                                    <div className="flex gap-2">
                                        <input
                                            className={input} placeholder="질문"
                                            value={f.question}
                                            onChange={(e) => setFaqs((p) => p.map((x, j) => j === i ? { ...x, question: e.target.value } : x))}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setFaqs((p) => p.filter((_, j) => j !== i))}
                                            className="px-3 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50"
                                        >삭제</button>
                                    </div>
                                    <textarea
                                        rows={2} className={input} placeholder="답변"
                                        value={f.answerMd}
                                        onChange={(e) => setFaqs((p) => p.map((x, j) => j === i ? { ...x, answerMd: e.target.value } : x))}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                <h3 className="text-sm font-extrabold text-gray-800">
                                    이미지 <span className="font-normal text-gray-400">대표 이미지와 대체텍스트는 발행 조건입니다</span>
                                </h3>
                                <div className="flex gap-2">
                                    <label className={`px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-bold cursor-pointer ${uploading ? 'opacity-40 pointer-events-none' : ''}`}>
                                        + 대표 이미지
                                        <input type="file" accept={ACCEPT_IMAGE} className="hidden" onChange={(e) => upload(e, 'hero')} />
                                    </label>
                                    <label className={`px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-bold cursor-pointer ${uploading ? 'opacity-40 pointer-events-none' : ''}`}>
                                        + 본문 이미지
                                        <input type="file" accept={ACCEPT_IMAGE} className="hidden" onChange={(e) => upload(e, 'gallery')} />
                                    </label>
                                </div>
                            </div>
                            <p className="mb-3 text-[11px] text-gray-500">{IMAGE_HINT}</p>
                            {(!data.media || data.media.length === 0) && (
                                <p className="text-xs text-gray-400">아직 이미지가 없습니다. 대표 이미지를 하나 올려야 발행됩니다.</p>
                            )}
                            <div className="space-y-3">
                                {(data.media ?? []).map((m) => (
                                    <div key={m.mediaId} className="flex flex-wrap items-center gap-2">
                                        <img
                                            src={`/uploads/${m.storageKey}`} alt=""
                                            className="w-14 h-14 object-cover rounded border border-gray-200 shrink-0"
                                        />
                                        <select
                                            className={`${input} w-28`} value={m.role}
                                            onChange={(e) => run(() => updateMediaRole(id, m.mediaId, e.target.value))}
                                        >
                                            {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </select>
                                        <input
                                            className={`${input} flex-1 min-w-[200px] ${String(m.altText).startsWith('[검수필요]') ? 'border-amber-400 bg-amber-50' : ''}`}
                                            defaultValue={m.altText}
                                            onBlur={(e) => {
                                                if (e.target.value !== m.altText) run(() => updateMediaAlt(id, m.mediaId, e.target.value));
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => run(() => detachMedia(id, m.mediaId), '이미지를 뗐습니다.')}
                                            className="px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50 rounded"
                                        >떼기</button>
                                    </div>
                                ))}
                            </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <label className={label} htmlFor="c-body">
                            본문 (Markdown) <span className="font-normal text-gray-400">{ko(form.bodyMd)}자</span>
                        </label>
                        <textarea
                            id="c-body" rows={18}
                            className={`${input} font-mono text-xs leading-relaxed`}
                            value={form.bodyMd} onChange={set('bodyMd')}
                        />
                    </div>
                </div>

                {/* ── 발행 조건 ─────────────────────────────────────────── */}
                <div className="lg:sticky lg:top-4 space-y-3">
                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <div className="flex items-baseline justify-between mb-3">
                            <h3 className="text-sm font-extrabold text-gray-800">발행 조건</h3>
                            <span className="text-xs font-bold">
                                <span className={gate?.fail ? 'text-red-600' : 'text-emerald-600'}>필수 {gate?.fail ?? 0}</span>
                                <span className="text-gray-300"> · </span>
                                <span className={gate?.warn ? 'text-amber-600' : 'text-gray-400'}>경고 {gate?.warn ?? 0}</span>
                            </span>
                        </div>
                        <p className="text-[11px] text-gray-500 mb-3">
                            저장하면 다시 판정합니다. 판정은 서버가 하며 API 를 직접 불러도 같은 규칙이 적용됩니다.
                        </p>
                        <ul className="space-y-1.5">
                            {(gate?.items ?? []).map((it) => (
                                <li key={it.code} className="flex gap-2 text-xs leading-snug">
                                    <span className={`shrink-0 font-bold ${
                                        it.ok ? 'text-emerald-600' : it.level === 'fail' ? 'text-red-600' : 'text-amber-600'
                                    }`}>
                                        {it.ok ? '✓' : it.level === 'fail' ? '✕' : '⚠'}
                                    </span>
                                    <span className={it.ok ? 'text-gray-400' : 'text-gray-800'}>
                                        {it.label}
                                        {!it.ok && it.detail && (
                                            <span className="block text-[11px] text-gray-500">{it.detail}</span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {data.legacySource && (
                        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-[11px] text-gray-600 leading-relaxed">
                            이 콘텐츠는 <b>{data.legacySource}</b> #{data.legacyId} 에서 이관됐습니다.
                            공개 사이트는 아직 레거시 쪽을 읽으므로, 여기서 발행해도 지금 사이트 표시는 바뀌지 않습니다.
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default ContentReviewEditor;
